// Configuración → Funciones avanzadas.
//
// Funciones que la empresa prende por su cuenta, para equipos que necesitan más
// que el flujo estándar. Distinto de `dev_tester`, que es un flag interno nuestro.
// Cada una vive como una llave booleana de `company_profiles.funciones_avanzadas`
// (ADR-2026-09-06-lanzamientos-modelo-de-datos).
import { useEffect, useState } from 'react';
import { ShieldCheck, Sparkles } from 'lucide-react';
import {
  guardarFuncionAvanzada,
  useFuncionesAvanzadas,
  type FuncionesAvanzadas as Llaves,
} from '../../hooks/useFuncionesAvanzadas';
import { Interruptor } from './Interruptor';
import { FormatoDeDescarga } from './FormatoDeDescarga';
import { OpcionesDelAgente } from './OpcionesDelAgente';

interface FuncionesAvanzadasProps {
  profile: { id_company?: string | null } | null;
  setError: (m: string | null) => void;
  setSuccess: (m: string | null) => void;
}

interface Funcion {
  llave: keyof Llaves;
  nombre: string;
  descripcion: string;
  /** Solo las funciones raíz: un modo se lee dentro de la card de su madre. */
  icono?: React.ReactNode;
  /**
   * Esta función es un MODO de otra: se muestra DENTRO de la card de la madre, separada
   * por una línea, y no se puede prender con la madre apagada
   * (ADR-2026-09-07-analisis-automatico-al-generar). Apagar la madre no borra la llave
   * del modo: queda inerte y vuelve sola al reactivarla.
   */
  dependeDe?: keyof Llaves;
  /**
   * Piso de plan propio, cuando la función pide más que el "plan pago" que esta sección
   * ya exige entera. Sin esto, la fila se ofrece a todos los que ven la sección.
   *
   * La usa el flotante del Agente, que es Pro/Ultra
   * (ADR-2026-09-14-el-agente-es-de-pro-y-ultra). Se ESCONDE en vez de mostrarse con
   * candado a propósito: un interruptor apagado que no se puede prender no dice nada de
   * lo que hay del otro lado, y quien quiera el Agente lo va a encontrar en la pantalla
   * de planes y no en Configuración. El piso de verdad no está acá sino en
   * `funcionAvanzadaActiva`; esto es que no se ofrezca lo que no se puede dar.
   */
  piso?: (plan?: string | null) => boolean;
}

const FUNCIONES: Funcion[] = [
  // "Pre-lanzamientos" salió de acá: Tienda online e Instagram son de todos, y el botón
  // de publicar aparece solo con una tienda conectada
  // (ADR-2026-10-16-tienda-online-e-instagram-para-todos).
  {
    llave: 'verificacion_calidad',
    nombre: 'Verificación de calidad',
    descripcion:
      'Un botón en cada imagen generada analiza qué tan fiel salió el producto respecto de sus fotos reales y te propone regenerar las que no pasan. Por ahora, en la computadora.',
    icono: <ShieldCheck className="w-5 h-5" />,
  },
  {
    llave: 'boton_flotante_chat',
    nombre: 'Atlas siempre a mano',
    descripcion:
      'Un botón flotante "Preguntále a Atlas" en la esquina inferior derecha, en todas las pantallas de la computadora. Abre el chat al costado para pedirle ayuda con lo que estés viendo: explica cada campo de un modo y te lo deja configurado.',
    icono: <Sparkles className="w-5 h-5" />,
  },
  {
    llave: 'verificacion_automatica',
    dependeDe: 'verificacion_calidad',
    nombre: 'Analizar cada imagen automáticamente',
    descripcion:
      'Apenas termina una imagen, el análisis corre solo: no hace falta tocar el botón en cada una. Solo se analiza lo que generás de ahora en más, no tus creaciones anteriores.',
  },
];

export default function FuncionesAvanzadas({
  profile,
  setError,
  setSuccess,
}: FuncionesAvanzadasProps) {
  const { funciones, cargando, plan } = useFuncionesAvanzadas();
  const [local, setLocal] = useState<Llaves>(funciones);
  const [guardando, setGuardando] = useState<string | null>(null);

  // El hook resuelve async: cuando llega el valor real, se adopta.
  useEffect(() => {
    if (!cargando) setLocal(funciones);
  }, [cargando, funciones]);

  const alternar = async (llave: keyof Llaves, valor: boolean) => {
    if (!profile?.id_company) return;
    setError(null);
    setGuardando(llave);
    // Optimista: el interruptor responde al toque y se revierte si falla.
    setLocal((prev) => ({ ...prev, [llave]: valor }));
    const ok = await guardarFuncionAvanzada(profile.id_company, llave, valor);
    setGuardando(null);
    if (!ok) {
      setLocal((prev) => ({ ...prev, [llave]: !valor }));
      setError('No se pudo guardar el cambio. Probá de nuevo.');
      return;
    }
    const nombre = FUNCIONES.find((f) => f.llave === llave)?.nombre ?? 'La función';
    setSuccess(valor ? `${nombre} activado` : `${nombre} desactivado`);
    setTimeout(() => setSuccess(null), 3000);
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-brand-black">Funciones avanzadas</h2>
      <p className="mt-1 text-sm text-neutral-500">
        Funciones para equipos que trabajan con catálogos grandes. Se activan por empresa y las ve
        todo el equipo.
      </p>

      <div className="mt-5 space-y-3">
        {/* Solo las funciones RAÍZ abren card: un modo de otra función
            (`dependeDe`) vive DENTRO de la card de su madre, porque es una opción de
            ella y no una función más del listado
            (ADR-2026-09-07-analisis-automatico-al-generar). */}
        {FUNCIONES.filter((f) => !f.dependeDe && (!f.piso || f.piso(plan))).map((f) => {
          const activo = local[f.llave] === true;
          const modos = FUNCIONES.filter((m) => m.dependeDe === f.llave);
          return (
            <div key={f.llave} className="rounded-2xl border border-neutral-200 py-4 px-5">
              <div className="flex items-start gap-4">
                {/* El fondo tenue va inline por lo mismo: `bg-brand-primary/10` no compila. */}
                <span
                  className={`shrink-0 grid place-items-center w-10 h-10 rounded-xl ${
                    activo ? 'text-brand-primary' : 'bg-neutral-100 text-neutral-400'
                  }`}
                  style={activo ? { backgroundColor: 'rgb(var(--brand-primary-rgb) / 0.10)' } : undefined}
                >
                  {f.icono}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-brand-black">{f.nombre}</p>
                  <p className="mt-1 text-sm leading-relaxed text-neutral-500">{f.descripcion}</p>
                </div>
                <Interruptor
                  activo={activo}
                  disabled={cargando || guardando === f.llave || !profile?.id_company}
                  etiqueta={f.nombre}
                  onChange={(v) => void alternar(f.llave, v)}
                />
              </div>

              {modos.map((m) => {
                const modoActivo = local[m.llave] === true;
                return (
                  <div
                    key={m.llave}
                    // Separada por una línea y sangrada hasta donde arranca el texto de la
                    // madre (icono 40px + gap 16px = pl-14): se lee como una opción de
                    // ella, no como otra función. Atenuada mientras la madre esté apagada.
                    className={`mt-4 flex items-start gap-4 border-t border-neutral-200 pt-4 pl-14 ${
                      activo ? '' : 'opacity-50'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-brand-black">{m.nombre}</p>
                      <p className="mt-1 text-sm leading-relaxed text-neutral-500">{m.descripcion}</p>
                    </div>
                    <Interruptor
                      // Con la madre apagada el modo no rige, así que tampoco se muestra
                      // encendido: lo que se ve y lo que hace la app son lo mismo.
                      activo={modoActivo && activo}
                      disabled={cargando || guardando === m.llave || !profile?.id_company || !activo}
                      etiqueta={m.nombre}
                      onChange={(v) => void alternar(m.llave, v)}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* No sale de `FUNCIONES` porque su interruptor NO es una llave de
            `funciones_avanzadas`: es el `activo` de la config de descarga, que además
            es por marca y no por empresa
            (ADR-2026-09-14-el-formato-de-descarga-vive-en-funciones-avanzadas). */}
        <FormatoDeDescarga setError={setError} />

        {/* Tampoco salen de `FUNCIONES`, por la misma razón y un paso más lejos: no son
            llaves de empresa NI de marca sino del usuario en este navegador
            (`localStorage`, vía `ChatAtlasContext`). Bajaron de la caja del Agente de la
            home — ADR-2026-09-24-el-motor-y-las-herramientas-viven-en-configuracion. */}
        <OpcionesDelAgente plan={plan} />
      </div>
    </div>
  );
}
